import axios from "axios"
import { UGC_PLATFORMS, DEFAULT_PLATFORM } from "./constants"
import { fetchRecentApps } from "../../utils/recentAppsApi"
import { normalizeUgcItem } from "./normalize"
import { okara_api } from "../../utils/variables"

// The UGC/socials intelligence service is a separate backend from the main
// `REACT_APP_APIKEY` host (see `utils/variables.js`), same as the direct
// `okara.keyquest.io` calls already used in Social Comparison - so it reuses
// the centralized `okara_api` base url rather than reading its own env var.
const UGC_API_BASE_URL = okara_api

const getPlatformConfig = (platformId) => {
  const platform = UGC_PLATFORMS.find(p => p.id === platformId)
  if (!platform || !platform.enabled || !platform.endpoint) {
    throw new Error(`UGC coverage for "${platformId}" isn't available yet.`)
  }
  return platform
}

const authHeaders = () => {
  const token = localStorage.getItem("authToken")
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// The filter bar's state uses camelCase (matching the rest of this codebase's
// JS conventions); the backend's query params are snake_case (YouTube:
// RECENT_VIDEO_FILTERS.md §2, Instagram: INSTAGRAM_REEL_API.md §2). A handful
// of keys are spelled the same in both directions already (`duration`,
// `sentiment`, `username`, `hashtag`, `q`) which is exactly why only those
// silently "worked" before this map existed for YouTube - everything else
// was being sent under a param name the endpoint doesn't recognise, and per
// §1 (both docs) an unrecognised param is just dropped rather than rejected,
// so it failed silently instead of erroring. One shared map covers both
// platforms' filter keys - a key the current platform's filter shape doesn't
// use is simply never present in `filters`, so nothing here needs to branch
// on platform.
const FILTER_PARAM_MAP = {
  contentType: "content_type",
  creatorSize: "creator_size",
  minViews: "min_views",
  maxViews: "max_views",
  publishedWithinDays: "published_within_days",
  minAgeDays: "min_age_days",
  minViewsPerDay: "min_views_per_day",
  gainingOnly: "gaining_only",
  isShort: "is_short",
  isLive: "is_live",
  hasTranscript: "has_transcript",
  analysisBasis: "analysis_basis",
  minConfidence: "min_confidence",
  // Instagram-only (INSTAGRAM_REEL_API.md §2)
  audioType: "audio_type",
  minLikes: "min_likes",
  minComments: "min_comments",
  minShares: "min_shares",
  minEngagementRate: "min_engagement_rate",
  isVerified: "is_verified",
  hasCaption: "has_caption",
  hasHashtags: "has_hashtags",
  mentionsApp: "mentions_app",
  // TikTok-only (TIKTOK_VIDEO_API.md §2) - `music`, `keyword`, `username`,
  // `hashtag` and `q` are already spelled the same both directions, so they
  // need no entry here.
  minSaves: "min_saves",
  minFollowers: "min_followers",
  maxFollowers: "max_followers",
  hasMusic: "has_music",
}

// Normalizes an axios error into something the UI can branch on directly -
// tier-gating (403), unknown app (404) and bad requests (400) all come back
// as a JSON body rather than a thrown network error, per the API's error table.
const asApiError = (error) => {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  const err = new Error(data.error || error.message || "Failed to load UGC content.")
  err.status = status
  err.data = data
  return err
}

/**
 * Fetch one page of verified UGC videos for an app. The first call for an app
 * (and any call after its 3-month refresh date) starts a background job on the
 * backend - see `job_status` handling in `hooks/useUgcVideos.js`.
 */
export async function fetchUgcVideos({
  appId,
  platform = DEFAULT_PLATFORM,
  page = 1,
  pageSize = 9,
  sort = "views",
  includeTranscript = false,
  includeMeta = false,
  includeFacets = false,
  filters = {},
} = {}) {
  if (!appId) throw new Error("appId is required")
  const platformConfig = getPlatformConfig(platform)

  const params = { app_id: appId, page, page_size: pageSize, sort }
  if (includeTranscript) params.include_transcript = true
  if (includeMeta) params.include_meta = true
  if (includeFacets) params.include_facets = true

  // Filters never 400 on the backend - an unrecognised value is just dropped
  // (RECENT_VIDEO_FILTERS.md §1) - so it's safe to forward whatever the UI
  // has set without validating shapes here. Empty/false/undefined values are
  // dropped client-side so they don't show up in `filters_applied` as noise.
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === false) return
    if (Array.isArray(value) && value.length === 0) return
    const paramKey = FILTER_PARAM_MAP[key] || key
    params[paramKey] = Array.isArray(value) ? value.join(",") : value
  })

  try {
    const response = await axios.get(`${UGC_API_BASE_URL}${platformConfig.endpoint}`, {
      params,
      headers: authHeaders(),
    })
    return normalizeUgcResponse(response.data, platform)
  } catch (error) {
    throw asApiError(error)
  }
}

// YouTube's envelope calls its list/counts `videos`/`total_videos`/
// `total_videos_unfiltered`; Instagram's calls the same things `reels`/
// `total_reels`/`total_reels_unfiltered` (RECENT_VIDEO_FILTERS.md §4 vs
// INSTAGRAM_REEL_API.md §4). TikTok's envelope reuses YouTube's naming
// exactly (TIKTOK_VIDEO_API.md §4), so it already falls out of the same
// `raw.videos`/`raw.total_videos` fallback below without a third branch.
// Normalizing all three onto `items`/`total_items`/`total_items_unfiltered`
// here means the rest of the app (the polling hook, the page, the cards)
// reads one shape regardless of which platform answered - each item itself
// is normalized via `normalizeUgcItem`.
const normalizeUgcResponse = (raw, platform) => {
  const rawItems = raw.videos || raw.reels || []
  return {
    ...raw,
    items: rawItems.map((item) => normalizeUgcItem(item, platform)),
    total_items: raw.total_videos ?? raw.total_reels ?? 0,
    total_items_unfiltered: raw.total_videos_unfiltered ?? raw.total_reels_unfiltered ?? 0,
  }
}

/**
 * Force a fresh run before the 3-month refresh window is due. POST-only by
 * design on the backend - `force_refresh` is silently ignored on GET so a
 * retry or hover-prefetch can never trigger paid work.
 */
export async function forceRefreshUgcVideos({ appId, platform = DEFAULT_PLATFORM }) {
  if (!appId) throw new Error("appId is required")
  const platformConfig = getPlatformConfig(platform)

  try {
    const response = await axios.post(
      `${UGC_API_BASE_URL}${platformConfig.endpoint}`,
      { app_id: appId, force_refresh: true },
      { headers: { "Content-Type": "application/json", ...authHeaders() } }
    )
    return response.data
  } catch (error) {
    throw asApiError(error)
  }
}

/**
 * The "apps you last searched here" sidebar list (RECENT_APPS_API.md) - each
 * platform's `recentAppsFeature` (constants.js) is the feature key that
 * platform's endpoint writes to as a side effect of
 * `fetchUgcVideos`/`forceRefreshUgcVideos`. YouTube and Instagram keep fully
 * independent lists, so this is fetched per-platform rather than once.
 * Read-only, open to every access level, and never starts work.
 */
export async function fetchUgcRecentApps({ limit = 10, platform = DEFAULT_PLATFORM } = {}) {
  const platformConfig = getPlatformConfig(platform)
  try {
    return await fetchRecentApps(platformConfig.recentAppsFeature, { limit, baseUrl: UGC_API_BASE_URL })
  } catch (error) {
    throw asApiError(error)
  }
}
